import React, { useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface ImageUploaderProps {
  onImageUpload: (imageUrl: string) => void;
  onUploadError?: (error: string) => void;
  currentImage?: string | null;
  productId?: string;
  disabled?: boolean;
  className?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUpload,
  onUploadError,
  currentImage,
  disabled = false,
  className = ''
}) => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await processFiles(e.target.files);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    await processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      onUploadError?.('Nessun file selezionato');
      return;
    }

    const file = files[0];
    
    // Validazione del file
    if (!file.type.startsWith('image/')) {
      onUploadError?.('Il file deve essere un\'immagine');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      onUploadError?.('L\'immagine non può superare 5MB');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `products/${fileName}`;

      // Nota: il client Supabase Storage non espone un callback di progresso
      // upload; uploadProgress resta a 0 finché la promise non si risolve.
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        if (uploadError.message.includes('already exists')) {
          throw new Error('Un file con questo nome esiste già');
        }
        throw uploadError;
      }

      // Ottieni l'URL pubblico dell'immagine
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      onImageUpload(publicUrl);
      
      // Reset del file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('Errore nel caricamento dell\'immagine:', err);
      onUploadError?.(err.message || 'Errore nel caricamento dell\'immagine');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeImage = () => {
    onImageUpload('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        Immagine del prodotto
      </label>
      
      {/* Area di Drag & Drop */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/60'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
          disabled={uploading || disabled}
        />
        
        {uploading ? (
          <div className="space-y-2">
            <div className="w-full bg-muted rounded-full h-2.5">
              <div 
                className="bg-primary h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-muted-foreground">
              Caricamento... {Math.round(uploadProgress)}%
            </p>
          </div>
        ) : currentImage ? (
          <div className="space-y-2">
            <div className="mx-auto w-24 h-24 border border-border rounded-lg overflow-hidden">
              <img 
                src={currentImage} 
                alt="Anteprima" 
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-sm font-semibold text-eco-foreground">Immagine caricata</p>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage();
                }}
                className="text-danger text-sm font-semibold hover:underline"
              >
                Rimuovi immagine
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <svg 
              className="mx-auto h-12 w-12 text-muted-foreground" 
              stroke="currentColor" 
              fill="none" 
              viewBox="0 0 48 48"
            >
              <path 
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </svg>
            <div>
              <p className="text-sm text-muted-foreground">
                <span className="text-primary font-semibold">Clicca per caricare</span> o trascina un'immagine
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, GIF fino a 5MB
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Messaggi di stato */}
      {uploading && (
        <p className="text-sm text-muted-foreground">Sto caricando l'immagine...</p>
      )}
    </div>
  );
};

export default ImageUploader;